/**
 * Vector bubble geometry — VERBATIM port of the approved reference
 * (bubble-creator-ref/src/utils/bubblePath.ts): a single SVG path combining
 * body + tail per bubble type, drawn in PAGE PIXEL coordinates (never a
 * stretched 0..100 box) so nothing ever distorts on resize.
 *
 * Only the input shape changed from the reference: `PathBubble` uses a
 * CENTER x/y (not top-left) and an ABSOLUTE tail tip, matching the geometry
 * the reference's own `Bubble` type used. `toPathBubble` bridges our stored
 * `MangaBubble` (fractions of the page, top-left x/y, tail relative to
 * center) into this shape.
 */
import type { MangaBubble, BubbleType } from './api';

export type PathBubbleType = 'speech' | 'thought' | 'scream' | 'whisper' | 'box';
export type PathTailStyle = 'straight' | 'curved';

export interface PathBubble {
  type: PathBubbleType;
  x: number; // center X, page px
  y: number; // center Y, page px
  w: number; // body width, page px
  h: number; // body height, page px
  tailX: number; // tail tip, ABSOLUTE page px
  tailY: number; // tail tip, ABSOLUTE page px
  tailWidth: number;
  tailStyle: PathTailStyle;
  /** Manual mirror of the curved tail's bow (on top of the automatic side). */
  tailFlip?: boolean;
  screamSpikes: number;
  borderRadius: number;
  /** When true, renders as if the tail tip sat at the body's own center —
   * i.e. a tail-LESS shape (and, for 'thought', no trailing circles either;
   * see `thoughtTailCircles`). Used for every non-topmost member of a fused/
   * bridged "balloon chain" group, so a connected chain shows exactly ONE
   * visible tail — see `computeTailOmissions`. */
  omitTail?: boolean;
}

/** Normalizes an angle to be between -PI and PI. */
function normalizeAngle(angle: number): number {
  while (angle > Math.PI) angle -= 2 * Math.PI;
  while (angle < -Math.PI) angle += 2 * Math.PI;
  return angle;
}

/**
 * Generates the SVG path 'd' attribute for a given PathBubble — same
 * geometry as the reference's `generateBubblePath` (box w/ tail injected on
 * an edge or a corner-fallback; scream/spiky star with the closest spike
 * stretched to the tip; thought cloud of Q-bumps; ellipse w/ a straight or
 * tapered curved tail for speech/whisper).
 */
export function generateBubblePath(bubble: PathBubble): string {
  const cx = bubble.x;
  const cy = bubble.y;
  const rx = bubble.w / 2;
  const ry = bubble.h / 2;
  // `omitTail` collapses the tail tip onto the body's own center — every
  // shape branch below already draws a plain tail-less body whenever the tip
  // falls inside/at the center (isTailInside/isTailOutside checks), so this
  // single override is enough to suppress the tail everywhere, no branch-
  // specific special-casing needed.
  const tx = bubble.omitTail ? bubble.x : bubble.tailX;
  const ty = bubble.omitTail ? bubble.y : bubble.tailY;

  // 1. NARRATOR BOX STYLE
  if (bubble.type === 'box') {
    const x_l = cx - rx;
    const x_r = cx + rx;
    const y_t = cy - ry;
    const y_b = cy + ry;
    const r = Math.min(bubble.borderRadius, rx, ry);

    // If tail tip is inside the box, or tail is not needed, draw simple rounded rect
    const isTailInside = tx >= x_l && tx <= x_r && ty >= y_t && ty <= y_b;
    if (isTailInside) {
      return `
        M ${x_l + r} ${y_t}
        L ${x_r - r} ${y_t}
        Q ${x_r} ${y_t} ${x_r} ${y_t + r}
        L ${x_r} ${y_b - r}
        Q ${x_r} ${y_b} ${x_r - r} ${y_b}
        L ${x_l + r} ${y_b}
        Q ${x_l} ${y_b} ${x_l} ${y_b - r}
        L ${x_l} ${y_t + r}
        Q ${x_l} ${y_t} ${x_l + r} ${y_t}
        Z
      `.trim().replace(/\s+/g, ' ');
    }

    // Determine which side the tail points to
    const tw = bubble.tailWidth;
    let path = '';

    // We'll build the rounded rectangle path and inject the tail on the appropriate edge
    const hasTopTail = ty < y_t && tx >= x_l && tx <= x_r;
    const hasBottomTail = ty > y_b && tx >= x_l && tx <= x_r;
    const hasLeftTail = tx < x_l && ty >= y_t && ty <= y_b;
    const hasRightTail = tx > x_r && ty >= y_t && ty <= y_b;

    // Corner fallback: if none of the above matches (diagonal tail),
    // we find the closest side and place the tail base there
    let placement = 'bottom';
    if (!hasTopTail && !hasBottomTail && !hasLeftTail && !hasRightTail) {
      const dx = tx - cx;
      const dy = ty - cy;
      if (Math.abs(dx) / rx > Math.abs(dy) / ry) {
        placement = dx > 0 ? 'right' : 'left';
      } else {
        placement = dy > 0 ? 'bottom' : 'top';
      }
    }

    const injectTop = hasTopTail || placement === 'top';
    const injectBottom = hasBottomTail || placement === 'bottom';
    const injectLeft = hasLeftTail || placement === 'left';
    const injectRight = hasRightTail || placement === 'right';

    // Build path clockwise starting from top-left corner
    // 1. Top Edge
    path += `M ${x_l + r} ${y_t} `;
    if (injectTop) {
      const tBaseX = Math.max(x_l + r + tw / 2, Math.min(x_r - r - tw / 2, tx));
      path += `L ${tBaseX - tw / 2} ${y_t} L ${tx} ${ty} L ${tBaseX + tw / 2} ${y_t} `;
    }
    path += `L ${x_r - r} ${y_t} `;
    path += `Q ${x_r} ${y_t} ${x_r} ${y_t + r} `;

    // 2. Right Edge
    if (injectRight) {
      const tBaseY = Math.max(y_t + r + tw / 2, Math.min(y_b - r - tw / 2, ty));
      path += `L ${x_r} ${tBaseY - tw / 2} L ${tx} ${ty} L ${x_r} ${tBaseY + tw / 2} `;
    }
    path += `L ${x_r} ${y_b - r} `;
    path += `Q ${x_r} ${y_b} ${x_r - r} ${y_b} `;

    // 3. Bottom Edge
    if (injectBottom) {
      const tBaseX = Math.max(x_l + r + tw / 2, Math.min(x_r - r - tw / 2, tx));
      path += `L ${tBaseX + tw / 2} ${y_b} L ${tx} ${ty} L ${tBaseX - tw / 2} ${y_b} `;
    }
    path += `L ${x_l + r} ${y_b} `;
    path += `Q ${x_l} ${y_b} ${x_l} ${y_b - r} `;

    // 4. Left Edge
    if (injectLeft) {
      const tBaseY = Math.max(y_t + r + tw / 2, Math.min(y_b - r - tw / 2, ty));
      path += `L ${x_l} ${tBaseY + tw / 2} L ${tx} ${ty} L ${x_l} ${tBaseY - tw / 2} `;
    }
    path += `L ${x_l} ${y_t + r} `;
    path += `Q ${x_l} ${y_t} ${x_l + r} ${y_t} Z`;

    return path.trim().replace(/\s+/g, ' ');
  }

  // 2. SCREAM / EXPLOSION (SPIKY) STYLE
  if (bubble.type === 'scream') {
    const N = bubble.screamSpikes || 16;
    const points: [number, number][] = [];
    const tailAngle = Math.atan2(ty - cy, tx - cx);
    const isTailOutside = ((tx - cx) / rx) ** 2 + ((ty - cy) / ry) ** 2 > 1.1;

    // Find closest spike index to the tail angle
    let closestSpikeIdx = -1;
    let minAngleDiff = Infinity;

    for (let i = 0; i < N; i++) {
      const a = (i * 2 * Math.PI) / N;
      const diff = Math.abs(normalizeAngle(a - tailAngle));
      if (diff < minAngleDiff) {
        minAngleDiff = diff;
        closestSpikeIdx = i * 2; // outer points are even indices
      }
    }

    for (let i = 0; i < 2 * N; i++) {
      const a = (i * Math.PI) / N;
      const isOuter = i % 2 === 0;
      const rFactor = isOuter ? 1.25 : 0.85;

      if (isOuter && i === closestSpikeIdx && isTailOutside) {
        // Stretch this spike to the tail tip
        points.push([tx, ty]);
      } else {
        // Normal outer/inner point
        const px = cx + rx * rFactor * Math.cos(a);
        const py = cy + ry * rFactor * Math.sin(a);
        points.push([px, py]);
      }
    }

    const d = points.map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${p[0]} ${p[1]}`).join(' ') + ' Z';
    return d;
  }

  // 3. THOUGHT CLOUD STYLE
  if (bubble.type === 'thought') {
    const N = 12; // number of cloud bumps
    const points: string[] = [];

    // Main cloud body path made of outward arcs
    for (let i = 0; i < N; i++) {
      const theta1 = (i * 2 * Math.PI) / N;
      const theta2 = ((i + 1) * 2 * Math.PI) / N;
      const thetaMid = ((i + 0.5) * 2 * Math.PI) / N;

      const x1 = cx + rx * Math.cos(theta1);
      const y1 = cy + ry * Math.sin(theta1);
      const x2 = cx + rx * Math.cos(theta2);
      const y2 = cy + ry * Math.sin(theta2);

      // Boost control point outwards to create bubbly cloud bump
      const ctrlX = cx + rx * 1.35 * Math.cos(thetaMid);
      const ctrlY = cy + ry * 1.35 * Math.sin(thetaMid);

      if (i === 0) {
        points.push(`M ${x1} ${y1}`);
      }
      points.push(`Q ${ctrlX} ${ctrlY} ${x2} ${y2}`);
    }

    return points.join(' ') + ' Z';
  }

  // 4. CLASSIC SPEECH / WHISPER STYLE (ELLIPSE)
  const isTailOutside = ((tx - cx) / rx) ** 2 + ((ty - cy) / ry) ** 2 > 1;

  if (!isTailOutside) {
    // Return simple ellipse path if tail is inside the bubble body
    return `M ${cx - rx} ${cy} A ${rx} ${ry} 0 1 0 ${cx + rx} ${cy} A ${rx} ${ry} 0 1 0 ${cx - rx} ${cy} Z`;
  }

  // Calculate tail attachment angles and points
  const angle = Math.atan2(ty - cy, tx - cx);

  // Calculate dynamic base angle based on the bubble size and tailWidth
  const baseAngle = Math.max(0.12, Math.min(0.45, bubble.tailWidth / Math.max(rx, ry)));

  const bx1 = cx + rx * Math.cos(angle - baseAngle);
  const by1 = cy + ry * Math.sin(angle - baseAngle);
  const bx2 = cx + rx * Math.cos(angle + baseAngle);
  const by2 = cy + ry * Math.sin(angle + baseAngle);

  // Large arc is true if we go around the majority of the ellipse (> 180 degrees)
  const d_body = `M ${bx2} ${by2} A ${rx} ${ry} 0 1 1 ${bx1} ${by1}`;

  // Add the tail path
  if (bubble.tailStyle === 'curved') {
    // Beautiful tapered curved tail pointing to speaker
    const dx = tx - cx;
    const dy = ty - cy;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const nx = -dy / len;
    const ny = dx / len;

    // Control point offset based on distance to make it look professional and
    // organic. The bow MIRRORS with the tip's horizontal side (comma reads
    // naturally whether the tail points left or right of the bubble); for
    // near-vertical tails, mirror with the vertical side instead.
    let sideFlip = Math.abs(dx) > Math.abs(dy) * 0.3 ? (dx >= 0 ? 1 : -1) : (dy >= 0 ? 1 : -1);
    if (bubble.tailFlip) sideFlip *= -1; // manual override from the style panel
    const offset = len * 0.18 * sideFlip;
    const c1x = (bx1 + tx) / 2 + nx * offset;
    const c1y = (by1 + ty) / 2 + ny * offset;
    const c2x = (bx2 + tx) / 2 + nx * offset;
    const c2y = (by2 + ty) / 2 + ny * offset;

    return `${d_body} Q ${c1x} ${c1y} ${tx} ${ty} Q ${c2x} ${c2y} ${bx2} ${by2} Z`;
  } else {
    // Clean straight dynamic tail
    return `${d_body} L ${tx} ${ty} L ${bx2} ${by2} Z`;
  }
}

/**
 * The thought bubble's 3 trailing circles (small → tiny) between the cloud
 * body and the tail tip — same math as the reference App.tsx (~L1067-1096).
 * Empty when the tip falls inside the cloud (nothing to trail toward).
 */
export function thoughtTailCircles(b: PathBubble): { cx: number; cy: number; r: number }[] {
  if (b.omitTail) return []; // suppressed tail (see `computeTailOmissions`) — no trail either
  const tx = b.tailX, ty = b.tailY, cx = b.x, cy = b.y;
  const rx = b.w / 2, ry = b.h / 2;
  const isTailOutside = ((tx - cx) / rx) ** 2 + ((ty - cy) / ry) ** 2 > 1;
  if (!isTailOutside) return [];
  // Trail from the CLOUD'S VISUAL EDGE toward the tip — the reference started at
  // the center, which parked the first circle on top of the cloud body. The
  // bumps peak at ~1.18× the base radii, so we exit at 1.22× plus a small gap.
  const angle = Math.atan2(ty - cy, tx - cx);
  const ex = cx + rx * 1.22 * Math.cos(angle);
  const ey = cy + ry * 1.22 * Math.sin(angle);
  const totalToTip = Math.hypot(tx - cx, ty - cy);
  const edgeDist = Math.hypot(ex - cx, ey - cy);
  // Tip hasn't cleared the cloud edge yet — no room for a trail.
  if (totalToTip <= edgeDist + b.tailWidth * 0.6) return [];
  const fractions = [0.18, 0.55, 0.9];
  const sizes = [0.7, 0.48, 0.3];
  // Thought trails CURVE like the speech tail (same comma bow): the circles ride
  // a quadratic bezier from the cloud edge to the tip, mirrored by the tip's
  // side (and by the manual tailFlip toggle). 'straight' keeps the line.
  if (b.tailStyle === 'straight') {
    return fractions.map((f, i) => ({
      cx: ex + (tx - ex) * f,
      cy: ey + (ty - ey) * f,
      r: Math.max(3, b.tailWidth * sizes[i]),
    }));
  }
  const dx = tx - cx, dy = ty - cy;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len, ny = dx / len;
  let sideFlip = Math.abs(dx) > Math.abs(dy) * 0.3 ? (dx >= 0 ? 1 : -1) : (dy >= 0 ? 1 : -1);
  if (b.tailFlip) sideFlip *= -1;
  const seg = Math.hypot(tx - ex, ty - ey);
  const bow = seg * 0.3 * sideFlip; // slightly stronger than the speech tail — reads better on dotted trails
  const ctrlX = (ex + tx) / 2 + nx * bow;
  const ctrlY = (ey + ty) / 2 + ny * bow;
  return fractions.map((f, i) => {
    const u = 1 - f;
    return {
      cx: u * u * ex + 2 * u * f * ctrlX + f * f * tx,
      cy: u * u * ey + 2 * u * f * ctrlY + f * f * ty,
      r: Math.max(3, b.tailWidth * sizes[i]),
    };
  });
}

// ── Shared bubble style/typography defaults ─────────────────────────────────
// Single source of truth for the classic look every renderer (editor canvas,
// internal reader, PNG/PDF export, HTML export) must reproduce identically —
// import these instead of re-deriving them locally.

export const MANGA_FONT = "'Comic Neue', 'Bangers', system-ui, sans-serif";

/** Single line-height every renderer (editor, reader, PNG/PDF export, HTML
 * export) MUST use — both to actually draw the text and to measure it in
 * `fitBubbleFontPx` below. Previously the editor's liquid auto-size measured
 * against 1.05 while every renderer painted at 1.25 (a drift that could let
 * auto-sized text slightly overflow its own box) — this is the one value now. */
export const BUBBLE_LINE_HEIGHT = 1.25;

/** Selectable text fonts for bubble content — 'comic' is the manga default;
 * stored on MangaBubble.fontFamily as the short key (portable, no CSS in data). */
export const BUBBLE_FONT_OPTIONS: { key: string; label: string; css: string }[] = [
  { key: 'comic', label: 'Comic', css: "'Comic Neue', sans-serif" },
  { key: 'display', label: 'Bangers', css: "'Bangers', cursive" },
  { key: 'serif', label: 'Fraunces', css: "'Fraunces', serif" },
];

/** Resolves a bubble's text font: an explicit `fontFamily` key wins, else the
 * type's usual default (shout is always Bangers, everything else the manga font). */
export function resolveFontFamily(type: BubbleType, fontFamily?: string): string {
  const picked = BUBBLE_FONT_OPTIONS.find((f) => f.key === fontFamily);
  if (picked) return picked.css;
  return type === 'shout' || type === 'sfx' ? "'Bangers', cursive" : MANGA_FONT;
}

/** Fill/stroke/text colours per bubble type (the classic, pre-vector-style look). */
export function bubbleBaseStyle(type: BubbleType): { fill: string; stroke: string; color: string } {
  switch (type) {
    case 'caption': return { fill: '#111', stroke: '#111', color: '#fff' };
    case 'narration': return { fill: '#faf7ef', stroke: '#111', color: '#111' };
    default: return { fill: '#fff', stroke: '#111', color: '#111' };
  }
}

/** Fill/stroke/text colours + stroke width for the vector renderer — the
 * (optional) explicit style fields win, falling back to the type's classic
 * look, so legacy bubbles without them keep rendering exactly as before. */
export function resolveBubbleStyle(b: MangaBubble): { fill: string; stroke: string; strokeWidth: number; textColor: string } {
  const base = bubbleBaseStyle(b.type);
  return {
    fill: b.fillColor ?? base.fill,
    stroke: b.strokeColor ?? base.stroke,
    strokeWidth: b.strokeWidth ?? 3,
    textColor: b.textColor ?? base.color,
  };
}

export function bubbleFontWeight(b: MangaBubble): number {
  if (b.fontStyle) return b.fontStyle.includes('bold') ? 700 : 400;
  return b.type === 'shout' ? 400 : 700;
}

export function bubbleFontStyleCss(b: MangaBubble): 'italic' | 'normal' {
  return b.fontStyle?.includes('italic') ? 'italic' : 'normal';
}

/** Fallback font size (px) when a bubble has no explicit `fontSize` — same
 * clamp() the editor has always used, calibrated against its own page-pixel
 * SVG coordinate space. Export renderers (real page-pixel canvas/SVG at any
 * page.height) rescale this by their own page height / reference height —
 * see `FONT_REFERENCE_HEIGHT` in manga-export.ts. */
/** Stable default font size in PAGE px — a CONSTANT per type (the reference app
 * used 16/20 on an 800px canvas), scaled only by the page width. Deliberately
 * NOT tied to the bubble's box: resizing a bubble no longer changes the text
 * size by itself — the fit only shrinks text when it genuinely doesn't fit. */
export function defaultBubbleFontPx(b: Pick<MangaBubble, 'type'>, pageW = 1000): number {
  const base = b.type === 'shout' ? 19 : 15;
  return Math.max(9, Math.min(34, base * (pageW / 800)));
}

/** Default inner padding in PAGE px — FIXED (reference: ~15px @ 800w), not a %
 * of the box width (wide captions used to get huge padding → tiny text). */
export function defaultBubblePaddingPx(b: Pick<MangaBubble, 'type'>, pageW = 1000): number {
  const base = b.type === 'shout' ? 18 : 13;
  return Math.round(base * (pageW / 800));
}

// ── Fit-to-box text sizing — SINGLE SOURCE OF TRUTH for the "never crop, never
// suppress" behaviour: every renderer (editor, reader, PNG/PDF export, HTML
// export) measures against the SAME hidden DOM element so the shrink point is
// identical everywhere. `preferredPx` (the user's explicit fontSize, or the
// `defaultBubbleFontPx` fallback) is a CEILING, never a target — the text only
// ever shrinks from it, down to `minPx`, and only as much as it needs to. ────
let fitMeasureEl: HTMLDivElement | null = null;
function getFitMeasureEl(): HTMLDivElement {
  if (fitMeasureEl) return fitMeasureEl;
  const el = document.createElement('div');
  el.style.position = 'fixed';
  el.style.visibility = 'hidden';
  el.style.pointerEvents = 'none';
  el.style.left = '-9999px';
  el.style.top = '0';
  el.style.whiteSpace = 'pre-wrap';
  el.style.overflowWrap = 'break-word';
  document.body.appendChild(el);
  fitMeasureEl = el;
  return el;
}

/**
 * Binary-searches (≈7 iterations, 0.5px granularity) the largest font size
 * ≤ `preferredPx` whose wrapped `text` fits within `contentHpx` at a fixed
 * `contentWpx` — measured against a hidden singleton DIV using the exact same
 * `white-space: pre-wrap; overflow-wrap: break-word` wrapping every renderer's
 * text layer uses. Never goes below `minPx` (the overflow:hidden clip on the
 * text box is the cinto de segurança past that point, not the line-clamp). SSR
 * guard: returns `preferredPx` unchanged when there's no `document`.
 */
export function fitBubbleFontPx(
  text: string,
  contentWpx: number,
  contentHpx: number,
  fontCss: string,
  fontWeight: string,
  fontStyleCss: string,
  preferredPx: number,
  minPx = 7,
  lineHeight: number = BUBBLE_LINE_HEIGHT,
): number {
  if (typeof document === 'undefined') return preferredPx;
  const safeMin = Math.min(minPx, preferredPx);
  if (contentWpx <= 0 || contentHpx <= 0) return Math.max(safeMin, preferredPx);

  const el = getFitMeasureEl();
  el.style.width = `${contentWpx}px`;
  el.style.fontFamily = fontCss;
  el.style.fontWeight = fontWeight;
  el.style.fontStyle = fontStyleCss;
  el.style.lineHeight = String(lineHeight);
  el.textContent = text || ' ';

  const fits = (px: number): boolean => {
    el.style.fontSize = `${px}px`;
    return el.scrollHeight <= contentHpx;
  };

  if (fits(preferredPx)) return preferredPx; // already fits — preferredPx is a ceiling, never a target
  if (!fits(safeMin)) return safeMin; // doesn't fit even at the floor — clamp there, overflow:hidden takes over

  let lo = safeMin, hi = preferredPx;
  for (let i = 0; i < 7; i++) {
    const mid = (lo + hi) / 2;
    if (fits(mid)) lo = mid; else hi = mid;
  }
  return Math.round(lo * 2) / 2; // 0.5px granularity
}

const TYPE_MAP: Record<BubbleType, PathBubbleType> = {
  speech: 'speech',
  thought: 'thought',
  shout: 'scream',
  narration: 'box',
  caption: 'box',
  whisper: 'whisper',
  // Never actually rendered — 'sfx' bubbles have NO vector body at all (see
  // `computeSfxLayout` and the 'sfx' branch every renderer checks first).
  // Present only so this Record stays exhaustive over `BubbleType`.
  sfx: 'box',
};

/**
 * Converts our stored `MangaBubble` (fractions of the page, top-left x/y,
 * tail relative to the bubble's own center) into the reference's `PathBubble`
 * shape (center x/y, absolute tail tip), both in PAGE PIXELS.
 *
 * When `b.tail` is absent, the tail collapses onto the center (tip == body
 * center) — `generateBubblePath` then draws a plain tail-less shape, which
 * matches our existing convention (narration/caption bubbles created without
 * a tail).
 *
 * `omitTail` (default false) forces the SAME tail-less rendering regardless
 * of `b.tail` — passed by every renderer for bubbles `computeTailOmissions`
 * flagged as a suppressed (non-topmost) member of a fused/bridged group.
 */
export function toPathBubble(b: MangaBubble, pageW: number, pageH: number, omitTail = false): PathBubble {
  const w = b.w * pageW;
  const h = b.h * pageH;
  const x = b.x * pageW + w / 2;
  const y = b.y * pageH + h / 2;
  const tailX = x + (b.tail?.x ?? 0) * pageW;
  const tailY = y + (b.tail?.y ?? 0) * pageH;
  const scale = pageW / 800;
  // Speech/whisper can opt into a square "manga panel" balloon body (keeping
  // the tail) via `bodyShape: 'rect'` — reuses the box renderer's own
  // edge-injected tail (already used by narration/caption), just applied to
  // a balloon type that actually has one.
  const isRectBody = (b.type === 'speech' || b.type === 'whisper') && b.bodyShape === 'rect';

  return {
    type: isRectBody ? 'box' : TYPE_MAP[b.type],
    x, y, w, h, tailX, tailY,
    tailWidth: b.tailWidth ?? (b.type === 'thought' ? 14 : 20) * scale,
    tailStyle: b.tailStyle ?? 'curved',
    tailFlip: b.tailFlip,
    screamSpikes: b.screamSpikes ?? 18,
    borderRadius: b.borderRadius ?? (b.type === 'narration' ? 8 : 12),
    omitTail,
  };
}

// ── SFX "letreiro" — giant onomatopoeia text with NO balloon body ──────────
export interface SfxLayout {
  cx: number; cy: number; boxW: number; boxH: number; rotation: number;
  lines: string[]; fontSizePx: number; fontFamily: string;
  fill: string; stroke: string; strokeWidth: number;
}

/**
 * Geometry + typography for an SFX bubble (type 'sfx') — huge display text
 * drawn straight over the art, no body/tail, meant to visually "leak" past
 * its own panel. Single source of truth shared by the editor, the internal
 * reader and both exports (canvas/PDF + HTML/SVG) so all four stay pixel
 * identical. Never auto-wrapped — one line, unless the text itself contains
 * `\n` (then stacked, each line sized so the whole block still fits `boxH`).
 */
export function computeSfxLayout(b: MangaBubble, pageW: number, pageH: number): SfxLayout {
  const boxW = Math.max(1, b.w * pageW), boxH = Math.max(1, b.h * pageH);
  const cx = b.x * pageW + boxW / 2, cy = b.y * pageH + boxH / 2;
  const rawLines = (b.text || 'BOOM').toUpperCase().split(/\r\n|\r|\n/).filter((l) => l.trim().length > 0);
  const lines = rawLines.length ? rawLines : ['BOOM'];
  const baseFontPx = b.fontSize ?? boxH * 0.8;
  const fontSizePx = Math.max(6, baseFontPx / lines.length);
  return {
    cx, cy, boxW, boxH, rotation: b.rotation ?? 0, lines, fontSizePx,
    fontFamily: resolveFontFamily(b.type, b.fontFamily),
    fill: b.fillColor ?? '#fff',
    stroke: b.strokeColor ?? '#111',
    strokeWidth: b.strokeWidth ?? 8,
  };
}

// ── Balloon fusion ("balloon chain") — bubbles that visually merge ─────────
/** Bubble types eligible to visually FUSE with a same-type/same-fill overlap
 * — the classic manga "balloon chain" splitting one long line across two+
 * touching balloons. Deliberately narrow: shout/thought/narration/caption/sfx
 * have shapes or a single-owner look that doesn't read well merged. */
const JOINABLE_TYPES = new Set<BubbleType>(['speech', 'whisper']);

function bboxOverlaps(a: MangaBubble, b: MangaBubble): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

/** Shortest gap, in PAGE PIXELS, between two (normalized-fraction) bounding
 * boxes — 0 when they overlap/touch. Needs real `pageW`/`pageH` (not just the
 * normalized fractions) because a page is rarely square: an x-gap and a
 * y-gap of the same normalized size are NOT the same physical distance. */
function bboxGapPx(a: MangaBubble, b: MangaBubble, pageW: number, pageH: number): number {
  const ax1 = a.x * pageW, ay1 = a.y * pageH, ax2 = (a.x + a.w) * pageW, ay2 = (a.y + a.h) * pageH;
  const bx1 = b.x * pageW, by1 = b.y * pageH, bx2 = (b.x + b.w) * pageW, by2 = (b.y + b.h) * pageH;
  const dx = Math.max(ax1 - bx2, bx1 - ax2, 0);
  const dy = Math.max(ay1 - by2, by1 - ay2, 0);
  return Math.hypot(dx, dy);
}

/** How close (in PAGE PIXELS) two eligible bubbles' bounding boxes must be to
 * bridge with a connector "neck" — scaled off an 800px-wide reference page,
 * same convention as every other size constant in this file. 30px @800w —
 * 25% farther than the original 24px, so balloons a bit further apart still
 * pick up a bridge instead of reading as two unrelated bubbles. */
export function bubbleProximityThresholdPx(pageW: number): number {
  return 30 * (pageW / 800);
}

/**
 * Groups bubbles that should render as ONE fused/connected organism: same
 * `BubbleType` (only speech/whisper are eligible), same EFFECTIVE fill
 * colour (explicit `fillColor`, else the type's classic default — via
 * `resolveBubbleStyle`, so an explicit colour still fuses with a same-looking
 * default one), and EITHER overlapping bounding boxes (classic "balloon
 * chain" fusion — bodies visually merge) OR bounding boxes within
 * `bubbleProximityThresholdPx` of each other (bodies stay separate but get a
 * bridging "neck", see `connectorPathsForGroup` — the classic manga double/
 * triple balloon). Union-find, so a touches/nears b touches/nears c chains
 * all three into one group even if a/c alone are neither overlapping nor
 * close. Returns arrays of INDICES into `bubbles`, one per group (singletons
 * included) — each group's indices stay in their original relative order
 * (draw/layer order), and groups themselves are ordered by their lowest
 * member index, keeping the overall layering close to the original array.
 * `pageW`/`pageH` are needed for the proximity check (see `bboxGapPx`) —
 * default to an 800×1131 reference page so callers that only care about the
 * (pageW/pageH-independent) overlap fusion can omit them.
 */
export function groupJoinedBubbles(bubbles: MangaBubble[], pageW = 800, pageH = 1131): number[][] {
  const n = bubbles.length;
  const parent = Array.from({ length: n }, (_, i) => i);
  const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i])));
  const union = (a: number, b: number) => {
    const ra = find(a), rb = find(b);
    if (ra !== rb) parent[ra] = rb;
  };

  const proximityPx = bubbleProximityThresholdPx(pageW);
  for (let i = 0; i < n; i++) {
    if (!JOINABLE_TYPES.has(bubbles[i].type)) continue;
    for (let j = i + 1; j < n; j++) {
      if (bubbles[j].type !== bubbles[i].type) continue;
      if (resolveBubbleStyle(bubbles[j]).fill !== resolveBubbleStyle(bubbles[i]).fill) continue;
      if (bboxOverlaps(bubbles[i], bubbles[j]) || bboxGapPx(bubbles[i], bubbles[j], pageW, pageH) <= proximityPx) {
        union(i, j);
      }
    }
  }

  const groups = new Map<number, number[]>();
  for (let i = 0; i < n; i++) {
    const root = find(i);
    let g = groups.get(root);
    if (!g) { g = []; groups.set(root, g); }
    g.push(i);
  }
  return [...groups.values()].sort((a, b) => a[0] - b[0]);
}

/** Bubble types that ever draw a visible tail — the ones `computeTailOmissions`
 * considers when deciding which member of a group keeps its tail. Caption/
 * narration/sfx never have one, so they never count toward (or get affected
 * by) the rule. */
const TAIL_BEARING_TYPES = new Set<BubbleType>(['speech', 'thought', 'shout', 'whisper']);

/**
 * Classic manga convention for a connected balloon chain: only ONE tail ever
 * points at the speaker — always the bubble that's HIGHEST on the page
 * (smallest box `y`, i.e. "mais acima"). Every OTHER tail-bearing member of
 * the SAME connected group (see `groupJoinedBubbles` — overlap-fused OR
 * proximity-bridged, both count) gets its tail suppressed.
 *
 * A group needs at least 2 TAIL-BEARING members for the rule to kick in
 * (`JOINABLE_TYPES` today only ever groups speech/whisper together, so in
 * practice every member of a size>1 group already has a tail-capable type —
 * this filter is just defensive/future-proof for whichever types end up
 * joinable later). Singleton groups are always left alone.
 *
 * Returns the SET of bubble `id`s that should render tail-less. Purely a
 * function of `bubbles`/`groups` — never `selected`/`editing`/drag state —
 * so selecting or dragging a suppressed bubble can never "revive" its tail
 * while the group still exists; the rule holds for as long as the group does.
 */
export function computeTailOmissions(bubbles: MangaBubble[], groups: number[][]): Set<string> {
  const omit = new Set<string>();
  for (const g of groups) {
    if (g.length < 2) continue;
    const tailMembers = g.filter((idx) => TAIL_BEARING_TYPES.has(bubbles[idx].type));
    if (tailMembers.length < 2) continue;
    let topIdx = tailMembers[0];
    for (const idx of tailMembers) if (bubbles[idx].y < bubbles[topIdx].y) topIdx = idx;
    for (const idx of tailMembers) if (idx !== topIdx) omit.add(bubbles[idx].id);
  }
  return omit;
}

// ── Balloon bridges ("connector necks") — bubbles that are CLOSE but not
// overlapping get a concave bridge between their bodies, the classic manga
// double/triple balloon (as opposed to the full-overlap fusion above, which
// erases the seam entirely). ──────────────────────────────────────────────

/** Point on the ellipse `(cx,cy,rx,ry)`'s boundary where a ray from its
 * center at `angle` (radians) exits — the PROPER ray/ellipse intersection
 * (unlike the naive `(rx*cos, ry*sin)` parametrization `generateBubblePath`
 * uses for the tail base, which traces the boundary but not along a ray at
 * that exact angle — fine for a tail's dynamic base, not precise enough for
 * a bridge that must visually touch the body along the center-to-center line). */
function ellipseEdgeExit(cx: number, cy: number, rx: number, ry: number, angle: number): { x: number; y: number } {
  const c = Math.cos(angle), s = Math.sin(angle);
  const denom = Math.sqrt((c * c) / (rx * rx) + (s * s) / (ry * ry)) || 1e-6;
  const t = 1 / denom;
  return { x: cx + t * c, y: cy + t * s };
}

/** Point on an axis-aligned box's boundary (half-extents `rx,ry` around
 * `cx,cy`) where a ray from its center at `angle` exits. */
function boxEdgeExit(cx: number, cy: number, rx: number, ry: number, angle: number): { x: number; y: number } {
  const c = Math.cos(angle), s = Math.sin(angle);
  const tx = c !== 0 ? rx / Math.abs(c) : Infinity;
  const ty = s !== 0 ? ry / Math.abs(s) : Infinity;
  const t = Math.min(tx, ty);
  return { x: cx + t * c, y: cy + t * s };
}

/** Outward "visual edge" factor per body type — thought/scream bumps/spikes
 * peak past their base ellipse (same 1.22× thought uses in `thoughtTailCircles`
 * for the same reason), so the bridge should touch there, not the base ellipse. */
function bubbleEdgeFactor(type: PathBubbleType): number {
  if (type === 'thought') return 1.22;
  if (type === 'scream') return 1.05;
  return 1;
}

/** Where a bridge should touch bubble `pb`'s body, coming from direction
 * `angle` (radians, center-to-center) — box types get a real box exit, every
 * other type an (outward-adjusted) ellipse exit. */
function bubbleEdgeExit(pb: PathBubble, angle: number): { x: number; y: number } {
  const rx = pb.w / 2, ry = pb.h / 2;
  if (pb.type === 'box') return boxEdgeExit(pb.x, pb.y, rx, ry, angle);
  const f = bubbleEdgeFactor(pb.type);
  return ellipseEdgeExit(pb.x, pb.y, rx * f, ry * f, angle);
}

/**
 * Half-thickness of `pb`'s own body PERPENDICULAR to the connecting axis
 * `angle` (radians) — i.e. how far the body's silhouette actually extends
 * sideways AT the point the bridge touches it. Reuses the exact same
 * `ellipseEdgeExit`/`boxEdgeExit` ray formulas `bubbleEdgeExit` uses (just
 * evaluated at `angle + 90°`, centered at the origin so the return value is
 * already the radius/magnitude). This is what makes the neck's WIDTH follow
 * the true connection angle: a horizontal chain bridges through each body's
 * height, a vertical chain through its WIDTH, a diagonal one through
 * whatever's in between — never a single fixed dimension. */
function crossSectionHalfExtent(pb: PathBubble, angle: number): number {
  const rx = pb.w / 2, ry = pb.h / 2;
  const perp = angle + Math.PI / 2;
  const p = pb.type === 'box' ? boxEdgeExit(0, 0, rx, ry, perp) : ellipseEdgeExit(0, 0, rx * bubbleEdgeFactor(pb.type), ry * bubbleEdgeFactor(pb.type), perp);
  return Math.hypot(p.x, p.y);
}

/**
 * Builds one bridge ("neck") path between two body-edge points `e1`→`e2` —
 * a quadrilateral of `2 * halfWidth` thickness, its two long edges each a
 * quadratic bézier pulled ~12% of the segment length TOWARD the centerline
 * at their midpoint, giving the classic organic "pinched waist" a manga
 * double-balloon neck reads as, instead of a stiff rectangle.
 */
function connectorBridgePath(e1: { x: number; y: number }, e2: { x: number; y: number }, halfWidth: number): string {
  const dx = e2.x - e1.x, dy = e2.y - e1.y;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len, ny = dx / len; // unit normal
  // Discreet inward pull (~15-20% of the neck's own FULL width, i.e. 2*halfWidth)
  // — just enough to read as a pinched "istmo" waist, not a bowtie. Capped by
  // `len * 0.3` so it can never overshoot on a very short bridge, and by
  // `halfWidth * 0.85` so the control point can never cross past the centerline
  // (which would self-intersect the quadrilateral).
  const waist = Math.min(halfWidth * 0.35, halfWidth * 0.85, len * 0.3);

  const topA = { x: e1.x + nx * halfWidth, y: e1.y + ny * halfWidth };
  const topB = { x: e2.x + nx * halfWidth, y: e2.y + ny * halfWidth };
  const topCtrl = { x: (topA.x + topB.x) / 2 - nx * waist, y: (topA.y + topB.y) / 2 - ny * waist };

  const botA = { x: e2.x - nx * halfWidth, y: e2.y - ny * halfWidth };
  const botB = { x: e1.x - nx * halfWidth, y: e1.y - ny * halfWidth };
  const botCtrl = { x: (botA.x + botB.x) / 2 + nx * waist, y: (botA.y + botB.y) / 2 + ny * waist };

  return `M ${topA.x} ${topA.y} Q ${topCtrl.x} ${topCtrl.y} ${topB.x} ${topB.y} `
    + `L ${botA.x} ${botA.y} Q ${botCtrl.x} ${botCtrl.y} ${botB.x} ${botB.y} Z`;
}

/**
 * SVG path 'd' strings for every bridge ("neck") between two members of a
 * fused/connected group (see `groupJoinedBubbles`) whose bounding boxes are
 * CLOSE but not overlapping — overlapping pairs already visually merge on
 * their own (no bridge drawn between them; a bridge would just double-paint
 * the already-shared area). Every renderer (editor, reader, PNG/PDF export,
 * HTML export) draws these with the SAME 2-pass technique as the fusion
 * itself: stroke pass (bodies + bridges), then a fill-only pass on top
 * (bodies + bridges) to erase every internal seam — see this module's own
 * callers for that split. `members` takes the STORED `MangaBubble` shape
 * (not `PathBubble`) so callers never have to convert twice — this converts
 * internally via `toPathBubble`, same bridge every other consumer of stored
 * bubbles crosses.
 *
 * One bridge PER PAIR of members whose bounding boxes are within the
 * proximity threshold — NOT one blob per group — so a 3+ balloon chain gets
 * a bridge per actual EDGE of the union (A–B, B–C, …), skipping any pair
 * that's too far apart even if they share a group through a third member.
 *
 * Each bridge is built entirely off the REAL axis between the two bodies
 * (`angle = atan2(center-to-center)`, works for horizontal, vertical or any
 * diagonal — nothing here assumes a horizontal layout):
 *  - Anchors: the true ray/body intersection on EACH shape along that exact
 *    axis (`bubbleEdgeExit` — proper ellipse/box exit, not the tail's own
 *    looser approximation), then pulled a couple px INSIDE each body (capped
 *    to ≤35% of the true edge-to-edge gap) so the fill-only pass overlaps
 *    both bodies' own fill and erases any hairline seam at the join, at any
 *    angle.
 *  - Width: driven by the GAP between the two bodies, NOT by either body's own
 *    size — `clamp(10px, gap*0.9, 26px)` at an 800px-wide reference page (scaled
 *    by `pageW`). This is what makes the bridge read as a proper "istmo": a
 *    thin neck whose LENGTH (the real edge-to-edge distance, above) always
 *    dominates its WIDTH, at any angle — sizing the width off the body itself
 *    (the old approach, via `crossSectionHalfExtent`) produced a short/fat bar
 *    lying across the gap instead of a stroke running along it. Still capped by
 *    each body's own true visual thickness perpendicular to the axis
 *    (`crossSectionHalfExtent` — never wider than either body's own
 *    cross-section) and by each body's smallest dimension (never swallows a
 *    small/thin balloon), and floored so it never collapses to a hairline.
 *  - Concavity: `connectorBridgePath`'s own quadratic pinch, already fully
 *    derived from `e1`→`e2` (so it's angle-agnostic on its own).
 * Recomputed fresh every call — nothing here is cached, so it redraws
 * perfectly as the user drags either bubble.
 */
export function connectorPathsForGroup(members: MangaBubble[], pageW: number, pageH: number): string[] {
  const paths: string[] = [];
  const threshold = bubbleProximityThresholdPx(pageW);
  const refScale = pageW / 800;
  for (let i = 0; i < members.length; i++) {
    for (let j = i + 1; j < members.length; j++) {
      const a = members[i], b = members[j];
      const gap = bboxGapPx(a, b, pageW, pageH);
      if (gap <= 0 || gap > threshold) continue; // overlapping (already fused) or too far for THIS pair
      const pa = toPathBubble(a, pageW, pageH);
      const pb = toPathBubble(b, pageW, pageH);
      const angle = Math.atan2(pb.y - pa.y, pb.x - pa.x);
      const rawE1 = bubbleEdgeExit(pa, angle);
      const rawE2 = bubbleEdgeExit(pb, angle + Math.PI);
      const rawLen = Math.hypot(rawE2.x - rawE1.x, rawE2.y - rawE1.y);
      const cosA = Math.cos(angle), sinA = Math.sin(angle);
      const insetPx = Math.min(2.5 * refScale, Math.max(0, rawLen) * 0.35);
      // e1 (near `a`) pulled BACK toward `a`'s own center; e2 (near `b`)
      // pushed FORWARD toward `b`'s own center — both along the same `angle`,
      // so the bridge's flat ends sit slightly INSIDE both bodies' silhouettes.
      const e1 = { x: rawE1.x - cosA * insetPx, y: rawE1.y - sinA * insetPx };
      const e2 = { x: rawE2.x + cosA * insetPx, y: rawE2.y + sinA * insetPx };

      // Neck width is driven by the GAP, not the bodies — a thin istmo whose
      // length (the real edge-to-edge distance) dominates its width at any
      // angle, instead of a bar sized off the balloons themselves.
      const neckWidthPx = Math.max(10 * refScale, Math.min(gap * 0.9, 26 * refScale));
      let halfWidth = neckWidthPx / 2;
      // Safety caps: never wider than either body's own true cross-section
      // (perpendicular thickness along this exact axis) or smallest dimension
      // (never swallows a small/thin balloon) — both defensive, gap-based
      // sizing already keeps halfWidth small in the normal case.
      const crossA = crossSectionHalfExtent(pa, angle);
      const crossB = crossSectionHalfExtent(pb, angle);
      halfWidth = Math.min(halfWidth, crossA, crossB);
      const minOwnDim = Math.min(pa.w, pa.h, pb.w, pb.h);
      halfWidth = Math.min(halfWidth, minOwnDim * 0.45);
      halfWidth = Math.max(halfWidth, 2.5 * refScale); // never collapse to a hairline
      paths.push(connectorBridgePath(e1, e2, halfWidth));
    }
  }
  return paths;
}
